/**
 * 客户端工具函数：在非 React 组件中获取翻译
 * 类似于 next-i18next 的 i18n.t，但适用于 next-intl
 */

import { findLocale, localeNormalizeMap } from '@/libs/find-locale';

// 翻译缓存
const translationCache = new Map<string, any>();

/**
 * 获取当前 locale 对应的翻译文件 locale
 */
function getFileLocale(locale: string): string {
  return localeNormalizeMap[locale] || locale;
}

/**
 * 动态加载翻译文件
 */
async function loadTranslation(locale: string): Promise<any> {
  const fileLocale = getFileLocale(locale);
  const cacheKey = fileLocale;

  // 检查缓存
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  try {
    // 动态导入翻译文件
    const messages = (await import(`@/locales/merged/${fileLocale}.json`)).default;
    translationCache.set(cacheKey, messages);
    return messages;
  } catch (error) {
    console.warn(`Translation file for ${error} not found, trying fallback`);
    // Fallback to en-US
    try {
      const messages = (await import('@/locales/merged/en-US.json')).default;
      translationCache.set(cacheKey, messages);
      return messages;
    } catch {
      // 如果连 en-US 都加载失败，返回空对象
      return {};
    }
  }
}

/**
 * 从嵌套对象中获取值（支持 'common.errors.networkError' 这样的路径）
 */
function getNestedValue(obj: any, path: string): string | undefined {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[key];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * 获取翻译文本
 * @param key 翻译键，支持嵌套路径，如 'common.errors.networkError'
 * @param locale 可选的 locale，如果不提供则从当前 URL 获取
 * @param fallback 可选的 fallback 文本，如果找不到翻译则返回此值
 * @returns 翻译后的文本
 */
export async function getTranslation(
  key: string,
  locale?: string,
  fallback?: string
): Promise<string> {
  // 只在客户端运行
  if (typeof window === 'undefined') {
    return fallback || key;
  }

  try {
    // 获取当前 locale
    const currentLocale = locale || findLocale(window.location.pathname);
    const fileLocale = getFileLocale(currentLocale);

    // 加载翻译文件
    const messages = await loadTranslation(fileLocale);

    // 获取翻译文本
    const translation = getNestedValue(messages, key);

    // 如果找到翻译，返回翻译文本
    if (translation) {
      return translation;
    }
  } catch (error) {
    console.warn(`Failed to get translation for key: ${key}`, error);
  }

  // 如果找不到翻译，返回 fallback 或 key
  return fallback || key;
}

/**
 * 同步获取翻译（使用缓存的翻译文件）
 * 注意：如果翻译文件还未加载，会返回 key 本身
 * @param key 翻译键
 * @param locale 可选的 locale
 * @returns 翻译后的文本或 key
 */
export function getTranslationSync(key: string, locale?: string): string {
  // 只在客户端运行
  if (typeof window === 'undefined') {
    return key;
  }

  // 获取当前 locale
  const currentLocale = locale || findLocale(window.location.pathname);
  const fileLocale = getFileLocale(currentLocale);

  // 从缓存获取翻译
  const messages = translationCache.get(fileLocale);
  if (!messages) {
    // 如果缓存中没有，返回 key
    // 可以触发异步加载（但不等待）
    loadTranslation(fileLocale).catch(() => { });
    return key;
  }

  // 获取翻译文本
  const translation = getNestedValue(messages, key);
  return translation || key;
}

